import { Application, Container, Graphics } from 'pixi.js';
import { BlockManager } from '@/canvas/systems/BlockManager';
import { CollisionHandler } from '@/canvas/systems/CollisionHandler';
import type { PhysicsEngine } from '@/canvas/systems/PhysicsEngine';

interface CreateGameSystemsParams {
  boxX: number;
  boxY: number;
  boxWidth: number;
  spawnOffsetAboveBox: number;
  container: Container;
  physicsEngine: PhysicsEngine;
  enableBlockMerge: boolean;
}

export const createPixiApp = async (
  screenWidth: number,
  screenHeight: number,
): Promise<Application> => {
  const app = new Application();
  await app.init({
    width: screenWidth,
    height: screenHeight,
    antialias: false,
    backgroundAlpha: 0,
    resolution: Math.min(window.devicePixelRatio || 1, 2),
    autoDensity: true,
  });
  return app;
};

export const createRootContainer = (app: Application): Container => {
  const container = new Container();
  app.stage.addChild(container);
  return container;
};

export const attachCanvasToDom = (
  containerElement: HTMLElement,
  canvas: HTMLCanvasElement,
): void => {
  containerElement.appendChild(canvas);
};

export const createGameSystems = ({
  boxX,
  boxY,
  boxWidth,
  spawnOffsetAboveBox,
  container,
  physicsEngine,
  enableBlockMerge,
}: CreateGameSystemsParams): {
  blockManager: BlockManager;
  collisionHandler: CollisionHandler;
} => {
  const blockManager = new BlockManager(
    boxX,
    boxY,
    boxWidth,
    spawnOffsetAboveBox,
  );
  const collisionHandler = new CollisionHandler(
    blockManager,
    container,
    physicsEngine,
    enableBlockMerge,
  );

  return { blockManager, collisionHandler };
};

export const createDebugGraphics = (container: Container): Graphics => {
  const graphics = new Graphics();
  container.addChild(graphics);
  return graphics;
};
